import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, Mic, MicOff, ChevronRight, ShieldCheck } from "lucide-react";
import api, { formatINR, waLink } from "../lib/api";
import { resolveImageUrl } from "./ImageUpload";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const STORAGE_KEY = "ydd_chat";

const isPackageSuggestion = (item) => item && typeof item === "object" && (item.slug || item.title);

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [session, setSession] = useState({});
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [botMode, setBotMode] = useState("live");
  const [correctingIdx, setCorrectingIdx] = useState(null);
  const [feedbackInput, setFeedbackInput] = useState("");
  const [quickReplies, setQuickReplies] = useState(["Birthday 🎉", "Romantic ❤️", "Under ₹3000 💸", "Talk to Team 👨‍💼"]);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const nav = useNavigate();
  const sendRef = useRef(null);

  useEffect(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSession({});
    setMessages([{ role: "bot", text: "" }]);
    setLoading(true);

    // Get AI-driven greeting on first load
    api.post("/chatbot", { message: "__GREET__", session: {} })
      .then(({ data }) => {
        setSession(data.session || {});
        setMessages([{ role: "bot", text: data.reply || "Namaste! 👋 Main hoon Priya, aapki decoration expert! Kaunsa occasion celebrate karna hai?", suggestions: data.suggestions || [] }]);
        setQuickReplies(data.quick_replies || ["Birthday 🎉", "Anniversary 💑", "Romantic ❤️", "Talk to Team 💬"]);
      })
      .catch(() => {
        setMessages([{ role: "bot", text: "Namaste! 👋 Main hoon Priya – YoursDreamDecoration ki sales expert! Aapka kaunsa occasion hai? 🎉" }]);
      })
      .finally(() => setLoading(false));

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = "en-IN";

      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => setIsListening(false);
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        if (sendRef.current) sendRef.current(transcript);
      };
      recognition.onerror = () => setIsListening(false);
      recognitionRef.current = recognition;
    }

    api.get("/config")
      .then(res => setBotMode(res.data.bot_mode || "live"))
      .catch(() => setBotMode("live"));
  }, []); // Run ONLY once on mount

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ session }));
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, session]);

  const send = React.useCallback(async (text) => {
    const userMsg = (text ?? input).trim();
    if (!userMsg) return;
    setMessages((m) => [...m, { role: "user", text: userMsg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chatbot", { message: userMsg, session });
      setSession(data.session || {});
      setMessages((m) => [...m, { role: "bot", text: data.reply, suggestions: data.suggestions || [] }]);
      setQuickReplies(data.quick_replies || []);
      if (data.actions?.length) {
        for (const a of data.actions) {
          if (a.type === "OPEN_PAGE" && a.url) setTimeout(() => nav(a.url), 600);
          if (a.type === "OPEN_WHATSAPP") window.open(waLink(a.message || "Hi"), "_blank");
        }
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: "Oops, kuch issue hua. Try again?" }]);
    } finally {
      setLoading(false);
    }
  }, [input, session, nav]);

  useEffect(() => {
    sendRef.current = send;
  }, [send]);


  const toggleListen = () => {
    if (!recognitionRef.current) {
      toast.error("Speech recognition not supported in this browser.");
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
    }
  };

  const submitFeedback = async () => {
    if (!feedbackInput.trim() || correctingIdx === null) return;
    const msg = messages[correctingIdx];
    const prevUserMsg = messages[correctingIdx - 1]?.text || "";
    try {
      await api.post("/chatbot/feedback", {
        query: prevUserMsg,
        bot_reply: msg.text,
        user_feedback: feedbackInput
      });
      toast.success("Feedback sent! Admin will review this. 🙏");
      setCorrectingIdx(null);
      setFeedbackInput("");
    } catch (e) {
      toast.error("Failed to send feedback.");
    }
  };

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-primary text-primary-foreground shadow-2xl flex items-center justify-center pulse-ring"
        data-testid="chatbot-toggle"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="w-8 h-8" />
            </motion.div>
          ) : (
            <motion.div key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle className="w-8 h-8" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            className="fixed bottom-24 right-4 sm:right-6 z-[100] w-[calc(100vw-2rem)] sm:w-[600px] h-[85vh] max-h-[850px] rounded-[3rem] bg-white border border-slate-200 shadow-3xl flex flex-col overflow-hidden"
            data-testid="chatbot-window"
          >
            <div className="px-5 py-5 bg-primary text-primary-foreground flex items-center gap-3 shadow-md relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12 blur-2xl" />
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-md border border-white/30">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-serif-display text-xl tracking-tight leading-none">Priya</div>
                <div className="text-[9px] uppercase tracking-[0.2em] opacity-80 font-bold flex items-center gap-1.5 mt-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> 
                  Online
                  <span className="mx-1 opacity-50">•</span>
                  <span className="px-1.5 py-0.5 bg-white/20 rounded-md border border-white/30 flex items-center gap-1 text-[8px]">
                    <ShieldCheck size={10} className="text-white" /> LOCAL AI
                  </span>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-8 space-y-6 no-scrollbar bg-slate-50/50">
              {messages.map((m, i) => (
                <motion.div
                  initial={{ opacity: 0, x: m.role === "user" ? 20 : -20, y: 10 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className={`max-w-[92%] group relative ${
                    m.role === "user" 
                      ? "bg-primary text-primary-foreground rounded-3xl rounded-tr-sm shadow-lg shadow-primary/10" 
                      : "bg-white border border-slate-100 shadow-sm rounded-3xl rounded-tl-sm text-slate-800"
                  } px-6 py-4 text-[15px] leading-relaxed`} data-testid={`chat-msg-${i}`}>
                    {m.text}
                    {m.suggestions?.length > 0 && (
                      <div className="mt-5 -mx-2 px-2 flex gap-4 overflow-x-auto no-scrollbar pb-2">
                        {m.suggestions.map((p) => (
                          isPackageSuggestion(p) ? (
                            <motion.button
                              whileHover={{ y: -8, shadow: "0 20px 25px -5px rgb(0 0 0 / 0.1)" }}
                              key={p.slug || p.title}
                              onClick={() => p.slug ? nav(`/package/${p.slug}`) : send(p.title)}
                              className="shrink-0 w-56 text-left bg-white border border-slate-100 rounded-[2rem] overflow-hidden shadow-sm transition-all duration-500"
                            >
                              <div className="relative h-32 overflow-hidden bg-slate-100 rounded-t-[2rem]">
                                {p.images?.[0] && (
                                  <img src={resolveImageUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                                )}
                                <div className="absolute top-2 right-2 px-3 py-1 bg-white/90 backdrop-blur rounded-full text-[10px] font-black text-primary uppercase">Best Seller</div>
                              </div>
                              <div className="p-4">
                                <div className="text-sm font-bold line-clamp-2 text-slate-800 leading-snug h-10">{p.title}</div>
                                <div className="flex items-center justify-between mt-3">
                                  <div className="text-base font-black text-primary">{p.price ? formatINR(p.price) : "Details"}</div>
                                  <div className="w-8 h-8 rounded-full bg-primary/5 flex items-center justify-center text-primary">
                                    <ChevronRight size={16} />
                                  </div>
                                </div>
                              </div>
                            </motion.button>
                          ) : (
                            <motion.button
                              whileHover={{ scale: 1.03 }}
                              key={String(p)}
                              onClick={() => send(String(p))}
                              className="shrink-0 px-4 py-2 rounded-full bg-primary/5 border border-primary/10 text-sm font-bold text-primary"
                            >
                              {String(p)}
                            </motion.button>
                          )
                        ))}
                      </div>
                    )}
                    {botMode === "training" && m.role === "bot" && (
                      <button 
                        onClick={() => { setCorrectingIdx(i); setFeedbackInput(""); }}
                        className="mt-3 text-[10px] text-primary/60 hover:text-primary hover:underline block border-t border-primary/5 pt-2 w-full text-left font-bold uppercase tracking-wider"
                      >
                        Improve
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
              
              {loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-2 ml-4">
                  {[0, 1, 2].map((dot) => (
                    <motion.div
                      key={dot}
                      animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.2, 1] }}
                      transition={{ duration: 1, repeat: Infinity, delay: dot * 0.2 }}
                      className="w-2 h-2 rounded-full bg-primary/30"
                    />
                  ))}
                </motion.div>
              )}

              {correctingIdx !== null && (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border-2 border-primary/20 rounded-[2.5rem] p-6 shadow-2xl">
                  <div className="font-serif-display text-xl text-primary mb-2">Refine Response</div>
                  <div className="text-slate-400 italic mb-4 text-xs leading-relaxed">"{messages[correctingIdx].text}"</div>
                  <textarea 
                    value={feedbackInput}
                    onChange={(e) => setFeedbackInput(e.target.value)}
                    placeholder="Provide a better response..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-4 text-sm focus:border-primary outline-none min-h-[100px] resize-none transition-all"
                  />
                  <div className="flex gap-3">
                    <button onClick={submitFeedback} className="flex-1 bg-primary text-primary-foreground py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:shadow-lg hover:shadow-primary/20 transition-all">Submit</button>
                    <button onClick={() => setCorrectingIdx(null)} className="px-6 text-slate-400 text-xs font-bold uppercase tracking-widest">Cancel</button>
                  </div>
                </motion.div>
              )}
            </div>

            <div className="p-6 space-y-5 bg-white border-t border-slate-100">
              {quickReplies?.length > 0 && (
                <div className="flex gap-2 overflow-x-auto no-scrollbar py-1">
                  {quickReplies.map((q) => (
                    <motion.button
                      whileHover={{ scale: 1.05, backgroundColor: "rgba(var(--primary), 0.05)" }}
                      whileTap={{ scale: 0.95 }}
                      key={q}
                      onClick={() => {
                        if (q.toLowerCase().includes("whatsapp") || q.toLowerCase().includes("team")) {
                          window.open(waLink(`Hi! I need help with decoration.`), "_blank");
                        } else {
                          send(q);
                        }
                      }}
                      className="shrink-0 px-5 py-2.5 rounded-full bg-slate-50 border border-slate-200 text-[13px] font-bold text-slate-600 hover:text-primary hover:border-primary/30 transition-all"
                    >
                      {q}
                    </motion.button>
                  ))}
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-3 items-center">
                <div className="flex-1 relative group">
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={isListening ? "Listening..." : "Type your message..."}
                    className={`w-full bg-slate-50 rounded-2xl pl-6 pr-12 py-4 text-[15px] outline-none border border-slate-100 focus:bg-white focus:border-primary/30 focus:shadow-xl focus:shadow-primary/5 transition-all duration-500 ${isListening ? "ring-4 ring-primary/10 bg-white" : ""}`}
                    disabled={isListening}
                  />
                  <button 
                    type="button"
                    onClick={toggleListen}
                    className={`absolute right-2 top-2 w-10 h-10 rounded-xl flex items-center justify-center transition-all ${isListening ? "bg-red-500 text-white shadow-lg" : "text-slate-300 hover:text-primary hover:bg-primary/5"}`}
                  >
                    {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </button>
                </div>
                <button 
                  type="submit" 
                  disabled={(!input.trim() && !isListening) || loading} 
                  className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50 transition-all shadow-xl shadow-primary/30 hover:scale-105 active:scale-95"
                >
                  <Send className="w-6 h-6" />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
